import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../components/Icon';
import { useUser } from '../../context/UserContext';

const TABS = [
  ['inducted', 'Inducted'],
  ['eligible', 'Ballot'],
  ['watchlist', 'Watchlist'],
];

const STATE_META = {
  inducted: { label: 'Hall of Famer', icon: '🏛️', color: '#f6c84c' },
  eligible: { label: 'Ballot Eligible', icon: '⭐', color: '#74d680' },
  watchlist: { label: 'On Watch', icon: '👀', color: '#73c9d4' },
};

function ResumeStat({ value, label }) {
  return <div className="lor-hof-stat"><strong>{value}</strong><span>{label}</span></div>;
}

function CandidateCard({ candidate, ballot, onVote, voting, currentUser }) {
  const meta = STATE_META[candidate.state] || STATE_META.watchlist;
  const resume = candidate.resume || {};
  return (
    <article className={`lor-card lor-hof-candidate lor-hof-candidate--${candidate.state}`}>
      <div className="lor-hof-candidate-head">
        <div>
          <div className="lor-hof-candidate-name">{candidate.display_name}</div>
          <div className="lor-hof-state" style={{ color: meta.color }}>
            <Icon emoji={meta.icon} size={13} /> {meta.label}
            {candidate.first_ballot && ' · First-ballot guarantee'}
            {candidate.is_former && ' · Former manager'}
          </div>
        </div>
        <div className="lor-hof-rings" aria-label={`${resume.championships || 0} championships`}>
          {resume.championships ? '🏆'.repeat(resume.championships) : '—'}
        </div>
      </div>

      <div className="lor-hof-stats">
        <ResumeStat value={resume.championships || 0} label="Titles" />
        <ResumeStat value={resume.finals || 0} label="Finals" />
        <ResumeStat value={resume.playoffs || 0} label="Playoffs" />
        <ResumeStat value={resume.seasons || 0} label="Seasons" />
        <ResumeStat value={resume.wins || 0} label="Wins" />
        <ResumeStat value={`${((candidate.win_pct || 0) * 100).toFixed(1)}%`} label="Win rate" />
      </div>

      <div className="lor-hof-case">
        <span>Why {candidate.state === 'inducted' ? 'inducted' : 'qualified'}</span>
        {(candidate.why || []).map(reason => <p key={reason}>✓ {reason}</p>)}
      </div>
      {candidate.championship_seasons?.length > 0 && (
        <div className="lor-hof-title-years">Championship seasons · {candidate.championship_seasons.join(' · ')}</div>
      )}
      {candidate.state === 'eligible' && ballot?.status === 'open' && (
        currentUser?.display_name === candidate.display_name ? (
          <div className="lor-hof-self-note">Nominees do not vote on their own candidacy.</div>
        ) : (
          <div className="lor-hof-vote-row">
            <button disabled={voting} className={ballot.my_votes?.[candidate.candidate_id] === true ? 'selected approve' : ''} onClick={() => onVote(candidate.candidate_id, true)}>Approve</button>
            <button disabled={voting} className={ballot.my_votes?.[candidate.candidate_id] === false ? 'selected decline' : ''} onClick={() => onVote(candidate.candidate_id, false)}>Decline</button>
          </div>
        )
      )}
    </article>
  );
}

export default function LORHallOfFame({ api }) {
  const { currentUser, csrfToken, refreshCsrf } = useUser();
  const [data, setData] = useState(null);
  const [active, setActive] = useState('inducted');
  const [error, setError] = useState('');
  const [ballot, setBallot] = useState(null);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    fetch(`${api}/api/lor/hall-of-fame`)
      .then(response => { if (!response.ok) throw new Error(String(response.status)); return response.json(); })
      .then(setData)
      .catch(() => setError('Hall of Fame data is temporarily unavailable.'));
  }, [api]);

  const loadBallot = () => fetch(`${api}/api/hall-of-fame/lor/ballot`, { credentials: 'include' })
    .then(response => response.ok ? response.json() : null)
    .then(setBallot)
    .catch(() => {});

  useEffect(() => { loadBallot(); }, [api]);

  const vote = async (candidateId, approve) => {
    setVoting(true);
    let token = csrfToken || await refreshCsrf();
    const response = await fetch(`${api}/api/hall-of-fame/lor/ballot/vote`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
      body: JSON.stringify({ candidate_id: candidateId, approve }),
    });
    if (response.ok) await loadBallot();
    else setError((await response.json().catch(() => ({}))).detail || 'Vote could not be recorded.');
    setVoting(false);
  };

  const candidates = useMemo(() => data?.[active] || [], [data, active]);
  if (error) return <div className="lor-empty">{error}</div>;
  if (!data) return <div className="lor-loading">Auditing Hall of Fame résumés…</div>;

  return (
    <div className="lor-hof-page">
      <header className="lor-hof-hero lor-card">
        <div className="lor-hof-kicker">League of Record · Est. 2012</div>
        <h2><Icon emoji="🏛️" size={25} /> Hall of Fame</h2>
        <p>The league's highest career honor. Eligibility comes from completed redraft results—not current rosters, projections or popularity.</p>
        <div className="lor-hof-asof">Audited through the {data.as_of_season || 'latest completed'} season</div>
      </header>

      <section className="lor-card lor-hof-rules">
        <div>
          <span>Guaranteed ballot</span>
          <strong>3 championships</strong>
        </div>
        <div>
          <span>League ballot</span>
          <strong>⅔ approval · 70% quorum</strong>
        </div>
        <div>
          <span>Annual class</span>
          <strong>0–1 inductee</strong>
        </div>
        <p>{ballot?.status === 'open' ? `Class of ${ballot.class_year} voting is open. Each vote may be changed until the commissioner closes the ballot.` : data.ballot?.status}</p>
      </section>

      <nav className="lor-hof-tabs" aria-label="Hall of Fame sections">
        {TABS.map(([key, label]) => (
          <button key={key} className={active === key ? 'active' : ''} onClick={() => setActive(key)}>
            {label}<b>{(data[key] || []).length}</b>
          </button>
        ))}
      </nav>

      <div className="lor-hof-grid">
        {candidates.map(candidate => <CandidateCard key={candidate.candidate_id} candidate={candidate} ballot={ballot} onVote={vote} voting={voting} currentUser={currentUser} />)}
        {candidates.length === 0 && <div className="lor-empty">No managers currently meet this stage.</div>}
      </div>

      <section className="lor-card lor-hof-process">
        <h3>How induction works</h3>
        <ol>
          <li>The system locks completed-season records after the championship.</li>
          <li>The league receives seven days to correct the evidence.</li>
          <li>Only managers who meet a published path reach the ballot; nominees cannot vote for themselves.</li>
          <li>The commissioner certifies the count but cannot add, remove or override an inductee.</li>
        </ol>
      </section>
    </div>
  );
}
